import { LoaderFunctionArgs } from "@remix-run/node";
import { useLoaderData, Link, useNavigate } from "@remix-run/react";
import { readFile } from "fs/promises";
import { join } from "path";
import { MarkdownRenderer } from "@/app/components/legal/MarkdownRenderer";
import { Button } from "@/app/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/components/ui/select";
import { ArrowLeft, Languages } from "lucide-react";
import {
  DOC_TYPE_MAP,
  LANGUAGE_OPTIONS,
  DOCUMENT_TITLES,
  LEGAL_CACHE_CONFIG,
  type Language,
} from "@/app/constants/legal";
import { ROUTES } from "@/app/utils/constants";
import {
  getLanguageFromCookie,
  toSupportedLanguage,
} from "@/app/utils/language";

// Simple in-memory cache for markdown content
const contentCache = new Map<string, { content: string; timestamp: number }>();

export async function loader({ params, request }: LoaderFunctionArgs) {
  const { docType } = params;

  if (!docType || !DOC_TYPE_MAP[docType]) {
    throw new Response("Document not found", { status: 404 });
  }

  // An explicit ?lang wins; otherwise the document opens in the language the
  // rest of the app is showing, so a German user is not dropped into English.
  const url = new URL(request.url);
  const language: Language = url.searchParams.has("lang")
    ? toSupportedLanguage(url.searchParams.get("lang"))
    : getLanguageFromCookie(request.headers.get("cookie"));

  const fileName = DOC_TYPE_MAP[docType];
  const cacheKey = `${fileName}-${language}`;

  // Check cache first
  const cached = contentCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < LEGAL_CACHE_CONFIG.TTL) {
    return {
      content: cached.content,
      docType,
      language,
      title: getDocumentTitle(docType, language),
    };
  }

  // Load from file system if not cached or expired
  const filePath = join(
    process.cwd(),
    "app",
    "content",
    `${fileName}-${language}.md`
  );

  try {
    const content = await readFile(filePath, "utf-8");

    // Update cache
    contentCache.set(cacheKey, { content, timestamp: Date.now() });

    return {
      content,
      docType,
      language,
      title: getDocumentTitle(docType, language),
    };
  } catch (error) {
    console.error("Failed to load legal document", {
      filePath,
      docType,
      language,
      fileName,
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });
    throw new Response("Document not found", { status: 404 });
  }
}

function getDocumentTitle(docType: string, language: Language): string {
  return DOCUMENT_TITLES[docType]?.[language] || docType;
}

export default function LegalDocument() {
  const { content, docType, language, title } = useLoaderData<typeof loader>();
  const navigate = useNavigate();

  const handleLanguageChange = (newLanguage: string) => {
    // Construct the route using the appropriate ROUTES helper based on docType
    let route: string;
    switch (docType) {
      case "privacy-policy":
        route = ROUTES.PRIVACY_POLICY({ lang: newLanguage });
        break;
      case "data-processing-agreement":
        route = ROUTES.DATA_PROCESSING_AGREEMENT({ lang: newLanguage });
        break;
      case "terms-and-conditions":
        route = ROUTES.TERMS_AND_CONDITIONS({ lang: newLanguage });
        break;
      default:
        route = `/legal/${docType}?lang=${newLanguage}`;
    }
    navigate(route);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header with Logo */}
      <header className="bg-card shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Back Button + Logo */}
            <div className="flex items-center gap-2 sm:gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm" className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
              </Link>

              <Link to="/" className="flex items-center gap-3">
                <img
                  src="/assets/logo.png"
                  alt="AiAuditBuddy Logo"
                  className="w-10 h-10 rounded-xl"
                />
                <h1 className="hidden sm:block text-lg font-semibold text-foreground">
                  AiAuditBuddy
                </h1>
              </Link>
            </div>

            {/* Right: Language Switcher */}
            <div className="flex items-center gap-2">
              <Languages className="h-4 w-4 text-muted-foreground" />
              <Select value={language} onValueChange={handleLanguageChange}>
                <SelectTrigger className="w-28 sm:w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGE_OPTIONS.map(lang => (
                    <SelectItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-card rounded-lg shadow-sm border p-8 lg:p-12">
          <h1 className="text-4xl font-bold mb-8 text-foreground">{title}</h1>

          <MarkdownRenderer
            content={content}
            className="prose prose-gray dark:prose-invert max-w-none"
          />
        </div>
      </main>
    </div>
  );
}
